import { isValidEmail } from "@/lib/validation/email";

const OEE_MARKER = "manual_upload@oee.com";
// Block control chars that would let an attacker inject extra SMTP headers.
const CONTROL_RE = /[\r\n\0]/;

/**
 * Lowercases the domain only — RFC 5321 §2.4 says local-parts must be
 * treated as case-sensitive, but every domain comparison is
 * case-insensitive. Preserving the local-part avoids breaking case-sensitive
 * inbound rules (e.g. plus-tag inboxes like `RFP+Marriott_Toronto@...`).
 */
function normalizeAddress(address: string): string {
  const at = address.lastIndexOf("@");
  if (at === -1) return address;
  return `${address.slice(0, at)}@${address.slice(at + 1).toLowerCase()}`;
}

/**
 * Parse a comma-separated email string into a clean, deduped array.
 * Strips trailing query strings (e.g. "?subject=..."), drops the
 * `manual_upload@oee.com` placeholder, blocks control chars used for
 * header injection, and discards anything that doesn't match a basic
 * email shape. Dedupe is case-insensitive but the returned addresses
 * preserve the original local-part case.
 *
 * @param raw - Comma-separated email string from venue_static.email
 *              or a chain rep `to` payload.
 * @returns Validated, order-preserving address list.
 */
export function parseEmailList(raw: string | null | undefined): string[] {
  if (typeof raw !== "string" || !raw) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(",")) {
    if (CONTROL_RE.test(part)) continue;
    const cleaned = part.split("?")[0].trim();
    if (!cleaned || cleaned.toLowerCase() === OEE_MARKER || !isValidEmail(cleaned)) continue;
    const normalized = normalizeAddress(cleaned);
    const key = normalized.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(normalized);
  }
  return out;
}

/**
 * Parse a single trusted email string. Useful for scalar request fields
 * (`reply_to`, single-element BCC) where the input is one address, not a
 * comma-separated list. Returns `null` if the input fails validation.
 *
 * @param raw - A single email address.
 * @returns Validated address with lowercased domain, or null if invalid.
 */
export function parseSingleEmail(raw: string | null | undefined): string | null {
  const [first] = parseEmailList(raw);
  return first ?? null;
}
